import { useEffect, useRef, useState } from "react"
import { Link } from "react-router-dom"
import { Helmet } from "react-helmet-async"
import { getPublicPlaceSummary } from "../services/chaperonConnector"
import { takeInitialPlace } from "../utilities/initialPlace"
import {
    LEVEL_COLORS,
    NO_DATA_COLOR,
    PERSONAL_SAFETY,
    RISK_CATEGORIES,
} from "../const/riskCategories"
import "./SharePreviewCard.css"

// The gauge fills to the middle of the level's band, never to the score,
// so the card shows no number even indirectly (same as the chaperon.app
// share card).
const LEVEL_FILL = { Safe: 90, Okay: 70, Caution: 50, Danger: 22 }

// The Analysis gauge's 240° arc: from lower left, over the top, to lower
// right (centre 100,104, radius 85).
const GAUGE_ARC = "M 26.39 146.5 A 85 85 0 1 1 173.61 146.5"

// What a logged-out visitor sees on /analysis/<slug>, for example after
// following a chaperon.app share link: the place's safety level, its
// categories' levels and overview, and a way to sign up or log in that
// returns them here. Public data only, so no numerical scores.
export default function SharePreviewCard({ slug }) {
    // On a server-rendered page the place is already in the HTML: render it
    // straight away, with no spinner and no second fetch.
    const initial = useRef(undefined)
    if (initial.current === undefined) initial.current = takeInitialPlace(slug)
    const [state, setState] = useState(() =>
        initial.current
            ? { status: "ready", place: initial.current }
            : { status: "loading", place: null },
    )

    useEffect(() => {
        // Only the first render can use the embedded data.
        if (initial.current?.slug === slug) {
            initial.current = null
            return
        }
        let cancelled = false
        setState({ status: "loading", place: null })
        getPublicPlaceSummary(slug)
            .then((place) => {
                if (!cancelled) {
                    setState({ status: place ? "ready" : "missing", place })
                }
            })
            .catch((error) => {
                console.error("Couldn't load the place summary:", error)
                if (!cancelled) setState({ status: "error", place: null })
            })
        return () => {
            cancelled = true
        }
    }, [slug])

    const place = state.place
    const level = place?.overall_level ?? null
    const where = place
        ? place.country && place.country !== place.name
            ? `${place.name}, ${place.country}`
            : place.name
        : null

    useEffect(() => {
        if (where && level) {
            document.title = `${where}: ${level.toUpperCase()} | Chaperon`
        }
    }, [where, level])

    const returnHere = { from: { pathname: `/analysis/${slug}` } }

    return (
        <div className="share-preview card mx-auto shadow-sm">
            {/* Address-level places stay out of search results (plan B2.2). */}
            {place?.type === "area" && (
                <Helmet>
                    <meta name="robots" content="noindex" />
                </Helmet>
            )}
            <div className="card-body text-center p-4">
                {state.status === "loading" && (
                    <div className="py-5">
                        <div className="spinner-border text-secondary" role="status">
                            <span className="visually-hidden">Loading…</span>
                        </div>
                    </div>
                )}

                {state.status === "ready" && (
                    <>
                        <div className="share-preview-gauge-wrap">
                            <svg
                                className="share-preview-gauge"
                                viewBox="0 0 200 160"
                                aria-hidden="true"
                            >
                                <path
                                    d={GAUGE_ARC}
                                    pathLength="100"
                                    className="share-preview-gauge-track"
                                />
                                <path
                                    d={GAUGE_ARC}
                                    pathLength="100"
                                    className="share-preview-gauge-fill"
                                    style={{
                                        stroke: LEVEL_COLORS[level] ?? NO_DATA_COLOR,
                                        strokeDasharray: `${LEVEL_FILL[level] ?? 0} 100`,
                                    }}
                                />
                            </svg>
                            <div className="share-preview-level">
                                <strong
                                    className={
                                        level?.length > 5 ? "share-preview-level-long" : undefined
                                    }
                                >
                                    {level ? level.toUpperCase() : "-"}
                                </strong>
                                <span>Safety Level</span>
                            </div>
                        </div>

                        <h2 className="h4 mt-2 mb-3">{where}</h2>

                        <div className="share-preview-icons mb-3">
                            {RISK_CATEGORIES.map((category, index) => {
                                const categoryLevel = place.levels?.[category.level] ?? null
                                return (
                                    <i
                                        key={category.level}
                                        className={`bi ${category.icon}`}
                                        style={{
                                            color: LEVEL_COLORS[categoryLevel] ?? NO_DATA_COLOR,
                                            animationDelay: `${0.8 + index * 0.08}s`,
                                        }}
                                        title={`${category.label}: ${categoryLevel ?? "no data"}`}
                                        aria-label={`${category.label}: ${categoryLevel ?? "no data"}`}
                                    ></i>
                                )
                            })}
                            <i
                                className={`bi ${PERSONAL_SAFETY.icon}`}
                                style={{
                                    color: NO_DATA_COLOR,
                                    animationDelay: `${0.8 + RISK_CATEGORIES.length * 0.08}s`,
                                }}
                                title={`${PERSONAL_SAFETY.label}: no data`}
                                aria-label={`${PERSONAL_SAFETY.label}: no data`}
                            ></i>
                        </div>

                        {place.overall_safety_overview && (
                            <p className="share-preview-overview text-start">
                                {place.overall_safety_overview}
                            </p>
                        )}
                    </>
                )}

                {state.status === "missing" && (
                    <p className="py-4 mb-0">
                        We don't have a safety analysis for this place yet.
                    </p>
                )}

                {state.status === "error" && (
                    <p className="py-4 mb-0">
                        We couldn't load this place right now. Please try again
                        shortly.
                    </p>
                )}

                {state.status !== "loading" && (
                    <div className="share-preview-actions">
                        <p className="text-muted small mb-3">
                            Sign up or log in for the full safety analysis
                        </p>
                        <div className="d-flex justify-content-center gap-2">
                            <Link
                                to="/user/register"
                                state={returnHere}
                                className="btn btn-dark"
                            >
                                Sign up
                            </Link>
                            <Link
                                to="/user/login"
                                state={returnHere}
                                className="btn btn-outline-dark"
                            >
                                Log in
                            </Link>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}
